/**
 * ComparePicker.jsx
 *
 * Compact "Compare Years" button + popover that lives next to the YearPicker
 * in the Topbar. Selecting 2+ years and clicking Apply flips the Dashboard
 * into side-by-side comparison mode (sets compareYears on AppContext).
 *
 * Visible only on the Dashboard route — compare mode is dashboard-specific.
 */
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../context/AppContext';
import { Icon } from '../ui/Icon';

const YEARS = [2026, 2025, 2024, 2023, 2022, 2021];

export function ComparePicker() {
  const { compareYears, setCompareYears } = useApp();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(compareYears && compareYears.length ? [...compareYears] : [2025, 2026]);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (open) setDraft(compareYears && compareYears.length ? [...compareYears] : [2025, 2026]);
  }, [open, compareYears]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    setOpen((v) => !v);
  };

  const toggleYear = (y) => {
    setDraft((cur) => (cur.includes(y) ? cur.filter((x) => x !== y) : [...cur, y]).sort());
  };

  const apply = () => {
    if (draft.length >= 2) setCompareYears([...draft].sort());
    else setCompareYears(null);
    setOpen(false);
  };

  const exitCompare = () => {
    setCompareYears(null);
    setOpen(false);
  };

  const active = compareYears && compareYears.length >= 2;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        title={active ? `Comparing ${compareYears.join(' vs ')}` : 'Compare years side-by-side'}
        className={[
          'flex items-center gap-2 px-3.5 py-2 rounded-md text-[12.5px] font-semibold transition-colors',
          active
            ? 'bg-gat-green text-white border border-gat-greenDarkest hover:bg-gat-greenLight'
            : 'bg-white border border-border text-text-dark hover:border-gat-green',
        ].join(' ')}
      >
        <Icon name="reporting" size={13} color={active ? '#fff' : '#1B5E20'} />
        <span>{active ? `Compare: ${compareYears.join(' · ')}` : 'Compare'}</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={popRef}
            className="fixed z-[1000] bg-white border border-border rounded-lg shadow-2xl w-[300px] text-text-dark"
            style={{ top: coords.top, right: coords.right }}
          >
            <div className="px-4 pt-3 pb-2 border-b border-border">
              <div className="text-[12px] font-bold text-text-dark">Compare years side-by-side</div>
              <div className="text-[10.5px] text-text-muted mt-0.5">Pick 2 or 3 years. Dashboard splits into one column per year.</div>
            </div>
            <div className="px-3 py-3 grid grid-cols-3 gap-2">
              {YEARS.map((y) => {
                const on = draft.includes(y);
                return (
                  <button
                    key={y}
                    type="button"
                    onClick={() => toggleYear(y)}
                    className={[
                      'px-3 py-2 rounded-md text-[12px] font-bold border-2 transition-all',
                      on
                        ? 'bg-gat-green text-white border-gat-greenDarkest shadow-sm'
                        : 'bg-white text-text-dark border-border hover:border-gat-green hover:bg-gat-green/5',
                    ].join(' ')}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
            <div className="px-3 pb-3">
              <div className="text-[10.5px] text-text-muted">
                Selected: <b className="text-text-dark">{draft.length ? draft.join(' · ') : 'none'}</b>
              </div>
            </div>
            <div className="flex justify-between items-center bg-surface-2 px-3 py-2 border-t border-border">
              {active ? (
                <button onClick={exitCompare} className="text-[11px] font-semibold text-rose-700 hover:text-rose-900">
                  Exit compare
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => setOpen(false)}
                  className="px-3 py-1 text-[11px] font-semibold text-text-body hover:bg-white rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={apply}
                  disabled={draft.length < 2}
                  className={[
                    'px-3 py-1 text-[11px] font-bold rounded',
                    draft.length < 2
                      ? 'bg-border-strong text-text-muted cursor-not-allowed'
                      : 'bg-gat-green text-white hover:bg-gat-greenLight',
                  ].join(' ')}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
